import { useGroups } from 'core/hooks/useGroups';
import * as coreEntities from 'core/store/index';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import { createAnalyticsService } from '../services';

/**
 * Custom hook for managing teacher analytics data
 * Fetches eligible disciplines from the backend and filters courses accordingly
 * @param {string} classId - The class/group ID to fetch analytics for
 */
const useTeacherAnalytics = () => {
  const { classId } = useParams();
  const { groups } = useGroups();
  const history = useHistory();

  const [analyticsData, setAnalyticsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [eligibleDisciplineIds, setEligibleDisciplineIds] = useState([]);

  // Get courses from Redux store
  const courses = useSelector((state) => coreEntities.courses.selectors.getCoursesOfGroup(state, classId));

  /**
   * Fetch eligible disciplines from the API
   * Extracts all discipline IDs from subjects that have activities
   */
  const fetchEligibleDisciplines = async () => {
    try {
      const analyticsService = createAnalyticsService();
      const response = await analyticsService.getEligibleSubjects();

      if (response?.data && Array.isArray(response.data)) {
        // Extract all discipline IDs from all subjects
        const disciplineIds = response.data.reduce((acc, subject) => {
          if (subject.disciplines && Array.isArray(subject.disciplines)) {
            const ids = subject.disciplines.map((discipline) => discipline.id);
            return [...acc, ...ids];
          }
          return acc;
        }, []);

        setEligibleDisciplineIds(disciplineIds);
      } else {
        // Empty response or invalid data
        setEligibleDisciplineIds([]);
      }
    } catch (error) {
      console.error('Failed to fetch eligible disciplines:', error);
      setEligibleDisciplineIds([]);
    }
  };

  // Fetch eligible disciplines on mount
  useEffect(() => {
    fetchEligibleDisciplines();
  }, []);

  // Filter courses based on eligible discipline IDs
  const eligibleCourses = useMemo(() => {
    if (!courses || !Array.isArray(courses)) return [];
    if (eligibleDisciplineIds.length === 0) return [];

    return courses
      .filter((course) => {
        const disciplineGuid = course.education_discipline_guid;
        return eligibleDisciplineIds.includes(disciplineGuid);
      })
      .map((course) => ({
        ...course,
        disciplineGuid: course.education_discipline_guid,
        disciplineName: course.education_discipline_name,
      }));
  }, [courses, eligibleDisciplineIds]);

  // Fetch analytics data when classId changes and courses are available
  useEffect(() => {
    let isMounted = true;

    if (!classId) {
      // Redirect to first group if no classId in URL
      if (groups?.schoolGroups?.length > 0) {
        const firstClassId = groups.schoolGroups[0]?.guid;
        history.replace(`/analytics/${firstClassId}`);
      }
      return;
    }

    if (eligibleCourses.length === 0) {
      if (isMounted) {
        setLoading(false);
      }
      return;
    }

    const fetchData = async () => {
      setLoading(true);

      try {
        const analyticsService = createAnalyticsService();
        const reports = await Promise.all(
          eligibleCourses.map(async (course) => {
            try {
              const report = await analyticsService.getClassDiagnosticReport(course.guid);
              return {
                courseId: course.guid,
                courseName: course.name,
                discipline: course.disciplineName || course.education_discipline_name,
                disciplineGuid: course.disciplineGuid || course.education_discipline_guid,
                disciplineName: course.disciplineName || course.education_discipline_name,
                report,
              };
            } catch (err) {
              console.error(`Failed to fetch analytics for course ${course.guid}:`, err);
              return null;
            }
          })
        );

        // Filter out failed fetches and courses with no analytics data
        const validReports = reports.filter((report) => {
          if (report === null) return false;
          const hasData = report.report?.unitMetrics && Array.isArray(report.report.unitMetrics) && report.report.unitMetrics.length > 0;
          return hasData;
        });

        if (isMounted) {
          setAnalyticsData(validReports);

          // Default to first course if available
          if (validReports.length > 0 && !selectedCourse) {
            setSelectedCourse(validReports[0].courseId);
          }
        }
      } catch (err) {
        console.error('Failed to fetch analytics data:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [classId, eligibleCourses.length]);

  // Get the currently selected report
  const currentReport = useMemo(() => {
    return analyticsData.find((data) => data.courseId === selectedCourse);
  }, [analyticsData, selectedCourse]);

  // Get recommendations (all units with their alerts if any)
  const recommendations = useMemo(() => {
    if (!currentReport || !currentReport.report || !currentReport.report.unitMetrics) {
      return [];
    }

    return currentReport.report.unitMetrics.map((unit) => {
      // Filter only lessons that have alerts
      const lessonsWithAlerts = unit.lessonMetrics?.filter((lesson) => lesson.hasAlerts) || [];

      return {
        unitId: unit.unitId,
        title: unit.title,
        order: unit.order,
        lessons: lessonsWithAlerts.map((lesson) => ({
          lessonId: lesson.lessonId,
          title: lesson.title,
          order: lesson.order,
          alertReasons: lesson.alertReasons || [],
        })),
        hasAlerts: lessonsWithAlerts.length > 0, // Add flag to indicate if unit has alerts
      };
    });
  }, [currentReport]);

  // Get class name from the first course
  const className = courses?.[0]?.school_group_name || '';

  return {
    loading,
    eligibleCourses,
    groups: groups?.schoolGroups || [],
    currentGroup: groups?.schoolGroups?.find((group) => group.guid === classId) || null,
    analyticsData,
    currentReport,
    recommendations,
    selectedCourse,
    setSelectedCourse,
    classId,
    className,
    hasMultipleCourses: eligibleCourses.length > 1,
  };
};

export default useTeacherAnalytics;
